import { StepType } from "@redotech/redo-model/advanced-flow/common";
import {
  ErrorHandlingStrategy,
  QueryOperator,
  RuleActionType,
  RuleConditionType,
  RuleTriggerType,
} from "@redotech/redo-model/advanced-flow/rules/types";
import { SchemaType } from "@redotech/redo-model/advanced-flow/schemas/schemas";
import { DefaultRule } from "../types";

/**
 * Default rule: Reopen on customer reply
 *
 * When a customer sends a message on a conversation that is not currently
 * OPEN or IN_PROGRESS, reopen the conversation (set status to OPEN).
 * Setting status to OPEN also covers the previously-deleted case, since
 * deletion is now represented purely by `status = 'DELETED'`.
 *
 * This rule has no additional configuration — it is toggled entirely via the
 * `enabled` flag on the `default_rule_config` row, so `configurations` is
 * always `{}`.
 */
export const reopenOnCustomerReplyRule = {
  id: "default-reopen-on-customer-reply",
  name: "Reopen on customer reply",
  description: "Reopen a conversation when a customer sends a message.",
  hidden: false,
  triggerType: RuleTriggerType.CONVERSATION_MESSAGE_CREATED,

  defaultConfig: {
    defaultRuleId: "default-reopen-on-customer-reply",
    enabled: true,
    sortIndex: 4,
    configurations: {},
  },

  template: {
    name: "Reopen on customer reply",
    description: "Reopen a conversation when a customer sends a message.",
    enabled: true,
    schemaType: SchemaType.RULES_V3,
    steps: [
      {
        type: StepType.TRIGGER,
        id: "trigger",
        schemaType: SchemaType.RULES_V3,
        category: "Rules",
        key: RuleTriggerType.CONVERSATION_MESSAGE_CREATED,
        nextId: "conditions",
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "conditions",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              conditionType: RuleConditionType.TICKET_STATUS,
              statuses: ["OPEN", "IN_PROGRESS"],
              queryOperator: QueryOperator.IS_NOT,
            },
          ],
        },
        nextTrueId: "actions",
      },
      {
        type: StepType.RULE_ACTIONS_V3,
        id: "actions",
        actions: [{ actionType: RuleActionType.SET_STATUS_V3, status: "OPEN" }],
        errorHandlingStrategy: ErrorHandlingStrategy.FAIL_FAST,
      },
    ],
  },
} as const satisfies DefaultRule;
